import { inject, Injectable } from '@angular/core';
import {
  CommonEnum,
  CommonRepository,
  FuncCheckByUserAcctParams,
  GetMenuListParams,
  InputTypeMapping,
  MenuList,
} from '@app/domain';
import { FileData, PresignedUrl } from '@app/shared/models/global.model';
import { DevFunctionCodeService } from '@app/shared/services/dev-function-code.service';
import { map, Observable, of } from 'rxjs';
import { CommonService } from '../api-clients';
import { CommonMapper } from '../mappers';

@Injectable()
export class CommonRepositoryImpl implements CommonRepository {
  private readonly _commonMapper = new CommonMapper();
  private readonly _devFunctionCodes = inject(DevFunctionCodeService);

  constructor(private readonly _commonService: CommonService) {}

  getEnums(): Observable<CommonEnum> {
    const commonEnumDTO = this._commonService.getEnums();
    return commonEnumDTO.pipe(
      map(dto => this._commonMapper.enumsToDomain(dto))
    );
  }

  presignedUrl(data: PresignedUrl): Observable<FileData[]> {
    const presignedUrlDTO = this._commonService.presignedUrl(data);
    return presignedUrlDTO.pipe(
      map(dto => this._commonMapper.fileDataToDomain(dto))
    );
  }

  generatePresignedUrl(fileKeys: string[]): Observable<FileData[]> {
    const presignedUrlDTO = this._commonService.generatePresignedUrl(fileKeys);
    return presignedUrlDTO.pipe(
      map(dto => this._commonMapper.fileDataToDomain(dto))
    );
  }

  downloadFile(fileKey: string, fileName: string): Observable<Blob> {
    return this._commonService.downloadFile(fileKey, fileName);
  }

  getInputTypeMapping(): Observable<InputTypeMapping[]> {
    const inputTypeMappingDTO = this._commonService.getInputTypeMapping();
    return inputTypeMappingDTO.pipe(
      map(dto => this._commonMapper.inputTypeMappingToDomain(dto))
    );
  }

  getMenuList(params: GetMenuListParams): Observable<MenuList[]> {
    const menuListDTO = this._commonService.getMenuList(params);
    return menuListDTO.pipe(
      map(dto => this._commonMapper.menuListToDomain(dto))
    );
  }

  funcCheckByUserAcct(
    params: FuncCheckByUserAcctParams
  ): Observable<Record<string, boolean>> {
    // Dev only: the picked function codes replace the function-check API
    if (this._devFunctionCodes.active()) {
      return of(this._devFunctionCodes.check(params.func));
    }

    const dto = this._commonService.funcCheckByUserAcct(params);
    return dto.pipe(
      map(dto => this._commonMapper.funcCheckByUserAcctToDomain(dto))
    );
  }
}
